import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { EventEmitterService } from '../../services/event-emitter.service';
import { BusinessesService } from '../../services/businesses.service';
import * as L from 'leaflet';
import { debounceTime, Subject, switchMap, tap } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { CountryISO } from 'ngx-intl-tel-input';
import { OrdersService } from '../../services/orders.service';
import { GeoSearchControl, OpenStreetMapProvider } from 'leaflet-geosearch';
import { BranchesService } from '../../services/branches.service';
import Currencies from '../../models/currencies.json';

@Component({
  standalone: false,
  selector: 'app-business-popup',
  templateUrl: './business-popup.component.html',
  styleUrl: './business-popup.component.scss',
})
export class BusinessPopupComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  title;
  type = '';
  formGroup: FormGroup;
  business;
  mymap: L.Map;
  marker;
  weekdays = [];
  parentBranches = [];
  selectedParentBranch = null;
  currencies: any = Currencies;
  paymentOptions = [
    { label: 'pay-on-delivery', value: 'pay-on-delivery' },
    // { label: 'pay-with-credit-card', value: 'pay-with-credit-card' },
  ];

  disableButton = false;
  selectedCountryISO: CountryISO;
  businessCategories = [];

  searchParentBranches = new Subject<string>();
  searchParentBranchesNoResults = 'no-results-found';

  constructor(
    public bsModalRef: BsModalRef,
    private businessesService: BusinessesService,
    private branchesService: BranchesService,
    private eventEmitterService: EventEmitterService,
    private translateService: TranslateService,
    private ordersService: OrdersService,
  ) {}

  async ngOnInit() {
    console.log('this.business:');
    console.log(this.business);
    this.setupWeekdays();
    await this.getAllParentBranches('');
    await this.getBusinessCategories();
    this.setupForm();
  }

  ngAfterViewInit(): void {
    if (this.type == 'new-business') {
      this.setupParentBranchesSearch();
      this.getUserLocation(true);
    }
  }

  ngOnDestroy() {
    if (this.mymap) {
      this.mymap.off();
      this.mymap.remove();
    }
  }

  setupWeekdays() {
    this.weekdays = [
      { id: 'monday', name: this.translateService.instant('monday') },
      { id: 'tuesday', name: this.translateService.instant('tuesday') },
      { id: 'wednesday', name: this.translateService.instant('wednesday') },
      { id: 'thursday', name: this.translateService.instant('thursday') },
      { id: 'friday', name: this.translateService.instant('friday') },
      { id: 'saturday', name: this.translateService.instant('saturday') },
      { id: 'sunday', name: this.translateService.instant('sunday') },
    ];
  }

  setupForm() {
    if (this.type == 'edit-business') {
      this.currencies.forEach((item) => {
        if (item.id == this.business.currency) {
          this.business.currency = item;
        }
      });

      // Set default payment options if not set
      if (!this.business.payment_options) {
        this.business.payment_options = ['pay-on-delivery'];
      } else if (typeof this.business.payment_options === 'string') {
        try {
          this.business.payment_options = JSON.parse(
            this.business.payment_options,
          );
        } catch (e) {
          this.business.payment_options = ['pay-on-delivery'];
        }
      }
    }

    if (this.type === 'new-business') {
      this.formGroup = new FormGroup({
        display_name: new FormControl(
          this.business ? this.business.display_name : '',
          Validators.required,
        ),
        name: new FormControl(this.business ? this.business.name : '', [
          Validators.required,
          Validators.pattern(/^[a-z0-9]+(?:[._-][a-z0-9]+)*$/),
        ]),
        bannerURL: new FormControl(
          this.business ? this.business.bannerURL : '',
        ),
        bannerFile: new FormControl(null),
        currency: new FormControl(
          this.business ? this.business.currency : this.currencies[1],
        ),
        payment_options: new FormControl(
          this.business ? this.business.payment_options : ['pay-on-delivery'],
          [Validators.required, Validators.minLength(1)],
        ),
        categories: new FormControl(
          this.business ? this.business.categories : [],
          [Validators.maxLength(3)],
        ),

        // branch fields
        phone: new FormControl('', Validators.required),
        latitude: new FormControl('', Validators.required),
        longitude: new FormControl('', Validators.required),
        start_hour: new FormControl('09:00', Validators.required),
        end_hour: new FormControl('17:00', Validators.required),
        off_days: new FormControl([]),
        parent_branch_id: new FormControl(null, Validators.required),
        delivery_radius: new FormControl(20, [
          Validators.required,
          Validators.min(0),
        ]),
        eta_min: new FormControl(20, [Validators.min(0)]),
        eta_max: new FormControl(30, [Validators.min(0)]),
        shipping_cost: new FormControl(0, [
          Validators.required,
          Validators.min(0),
        ]),
        min_order: new FormControl(0, [Validators.min(0)]),
      });
    }
    if (this.type === 'edit-business') {
      this.formGroup = new FormGroup({
        display_name: new FormControl(
          this.business ? this.business.display_name : '',
          Validators.required,
        ),
        name: new FormControl(this.business ? this.business.name : '', [
          Validators.required,
          Validators.pattern(/^[a-z0-9]+(?:[._-][a-z0-9]+)*$/),
        ]),
        bannerURL: new FormControl(
          this.business ? this.business.bannerURL : '',
        ),
        bannerFile: new FormControl(null),
        currency: new FormControl(this.business ? this.business.currency : ''),
        payment_options: new FormControl(
          this.business ? this.business.payment_options : ['pay-on-delivery'],
          [Validators.required, Validators.minLength(1)],
        ),
        categories: new FormControl(
          this.business ? this.business.categories : [],
          [Validators.maxLength(3)],
        ),
      });
    }

    if (this.type == 'new-business') {
      if (this.parentBranches.length == 1) {
        this.selectedParentBranch = this.parentBranches[0];
        this.formGroup
          .get('parent_branch_id')
          .setValue(this.parentBranches[0].id);
      }
    }

    if (this.type == 'edit-business') {
      const categoryIds = JSON.parse(this.business.categories);
      this.business.categories = this.businessCategories.filter(
        (category: any) => categoryIds.includes(category.id),
      );
      this.formGroup.get('categories').setValue(this.business.categories);
    }

    this.formGroup.get('name').valueChanges.subscribe((value: string) => {
      if (!value) {
        return;
      }
      const formatted = value.toLowerCase().replace(/ /g, '_');
      if (formatted !== value) {
        this.formGroup.get('name').setValue(formatted, { emitEvent: false });
      }
    });
  }

  getAllParentBranches(search) {
    return new Promise((resolve) => {
      const apiData: any = {};
      if (search && search.trim().length > 0) {
        apiData.search = search;
      }
      this.branchesService.getAllParentBranches(apiData).subscribe(
        (response: any) => {
          console.log('response:');
          console.log(response);
          this.searchParentBranchesNoResults = 'no-results-found';
          this.parentBranches = response.items;
          resolve(true);
        },
        (error) => {
          console.log('error:');
          console.log(error);
          this.searchParentBranchesNoResults = 'no-results-found';
          resolve(false);
        },
      );
    });
  }

  setupParentBranchesSearch() {
    this.searchParentBranches
      .pipe(
        tap(() => (this.searchParentBranchesNoResults = 'loading')),
        debounceTime(3000),
        switchMap(async (searchText) => this.getAllParentBranches(searchText)),
      )
      .subscribe();
  }

  getBusinessCategories() {
    return new Promise((resolve) => {
      this.businessesService.getAllBusinessCategories({}).subscribe(
        (response: any) => {
          this.businessCategories = response.filter(
            (category: any) => category.deletable,
          );
          resolve(true);
        },
        (error) => {
          console.log('error:');
          console.log(error);
          resolve(false);
        },
      );
    });
  }

  closeModal() {
    this.bsModalRef.hide();
  }

  setOffDays(event) {
    this.formGroup
      .get('off_days')
      .setValue(Array.isArray(event.value) ? event.value : []);
  }

  clearOffDays() {
    this.formGroup.get('off_days').setValue([]);
  }

  setParentBranch(event) {
    this.selectedParentBranch = event.value;
    this.formGroup
      .get('parent_branch_id')
      .setValue(event.value ? event.value.id : null);
  }

  setCurrency(event) {
    this.formGroup.get('currency').setValue(event.value);
  }

  buttonClicked() {
    this.formGroup.markAllAsTouched();

    if (this.formGroup.invalid) {
      return;
    }

    const formValue = { ...this.formGroup.value };
    formValue.off_days = Array.isArray(formValue.off_days)
      ? formValue.off_days.map((day) => day.id)
      : [];

    if (this.disableButton == true) {
      return;
    }
    this.disableButton = true;

    const formData = new FormData();

    const apiData = { ...formValue };
    apiData.currency = this.formGroup.get('currency').value.id;
    apiData.payment_options = JSON.stringify(apiData.payment_options);
    apiData.categories = JSON.stringify(
      (apiData.categories || []).map((category: any) => category.id),
    );
    apiData.phone = JSON.stringify(apiData.phone);
    apiData.off_days = JSON.stringify(apiData.off_days);

    let bannerURL = JSON.stringify(apiData.bannerURL);
    bannerURL = JSON.parse(bannerURL);

    if (this.isBase64(bannerURL)) {
      bannerURL = '';
    }

    Object.keys(apiData).forEach((key) => {
      if (key === 'bannerURL') {
        formData.append(key, bannerURL);
      } else if (key !== 'plan') {
        formData.append(key, apiData[key]);
      }
    });

    console.log('formData:');
    console.log(formData);

    if (this.type === 'new-business') {
      this.businessesService.createBusiness(formData).subscribe(
        (response) => {
          console.log('response:');
          console.log(response);
          this.eventEmitterService.callAllBusinesses();
          this.closeModal();
          this.disableButton = false;
        },
        (error) => {
          console.log('error:');
          console.log(error);
          this.disableButton = false;
        },
      );
    }
    if (this.type === 'edit-business') {
      this.businessesService
        .updateBusiness(this.business.id, formData)
        .subscribe(
          (response) => {
            console.log('response:');
            console.log(response);
            this.eventEmitterService.callAllBusinesses();
            this.closeModal();
            this.disableButton = false;
          },
          (error) => {
            console.log('error:');
            console.log(error);
            this.disableButton = false;
          },
        );
    }
  }

  async setupMap() {
    if (this.mymap) {
      this.mymap.remove();
    }

    this.mymap = L.map('map', {
      center: [this.formGroup.value.latitude, this.formGroup.value.longitude],
      zoom: 10,
      renderer: L.canvas(),
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap',
      className: 'map-tiles',
    }).addTo(this.mymap);

    const searchForAddress =
      this.translateService.instant('search-for-address');

    const provider = new OpenStreetMapProvider();
    const searchControl = GeoSearchControl({
      provider: provider,
      style: 'bar',
      showMarker: false,
      showPopup: false,
      autoClose: true,
      retainZoomLevel: false,
      animateZoom: true,
      keepResult: true,
      searchLabel: searchForAddress,
    });
    this.mymap.addControl(searchControl);

    const markerIcon = L.icon({
      iconUrl: '../../../assets/map-marker.svg',
      iconSize: [30, 30],
    });

    this.marker = L.marker(
      [this.formGroup.value.latitude, this.formGroup.value.longitude],
      {
        icon: markerIcon,
        draggable: true,
      },
    ).addTo(this.mymap);

    this.mymap.on('click', (e) => {
      var latlng = e.latlng;

      this.formGroup.get('latitude').setValue(latlng.lat);
      this.formGroup.get('longitude').setValue(latlng.lng);

      this.marker.setLatLng([
        this.formGroup.value.latitude,
        this.formGroup.value.longitude,
      ]);
      this.updateSelectedCountryISO(latlng.lat, latlng.lng);
    });

    this.marker.on('dragend', (e) => {
      var latlng = e.target.getLatLng();

      this.formGroup.get('latitude').setValue(latlng.lat);
      this.formGroup.get('longitude').setValue(latlng.lng);
      this.updateSelectedCountryISO(latlng.lat, latlng.lng);
    });

    this.mymap.on('geosearch/showlocation', (e: any) => {
      const { location } = e;
      this.marker.setLatLng([location.y, location.x]);
      this.formGroup.get('latitude').setValue(location.y);
      this.formGroup.get('longitude').setValue(location.x);
      this.updateSelectedCountryISO(location.y, location.x);
    });

    setTimeout(() => {
      this.mymap.invalidateSize();
    }, 30000);
  }

  getUserLocation(useData) {
    // If editing an existing business with coordinates, use those values for the map
    if (useData) {
      if (this.business && this.business.latitude && this.business.longitude) {
        this.setupMap();
        this.updateSelectedCountryISO(
          this.business.latitude,
          this.business.longitude,
        );
        return;
      }
    }

    // Otherwise get user location for new businesses
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position: GeolocationPosition) => {
          this.formGroup.get('latitude').setValue(position.coords.latitude);
          this.formGroup.get('longitude').setValue(position.coords.longitude);
          this.setupMap();
          this.updateSelectedCountryISO(
            position.coords.latitude,
            position.coords.longitude,
          );
        },
        (error: GeolocationPositionError) => {
          console.log('error:');
          console.log(error);
          this.formGroup.get('latitude').setValue(42.6635);
          this.formGroup.get('longitude').setValue(21.1638);
          this.setupMap();
          this.updateSelectedCountryISO(52.52, 13.405);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        },
      );
    } else {
      console.log('Geolocation is not supported by this browser.');
      this.formGroup.get('latitude').setValue(42.6635);
      this.formGroup.get('longitude').setValue(21.1638);
      this.setupMap();
      this.updateSelectedCountryISO(52.52, 13.405);
    }
  }

  async updateSelectedCountryISO(latitude: number, longitude: number) {
    try {
      const response = await this.ordersService.getNominatimAddress(
        latitude,
        longitude,
      );

      if (response && response.address && response.address.country_code) {
        const countryCode = response.address.country_code.toLowerCase();

        // Convert country code to CountryISO enum value
        // CountryISO enum uses lowercase country codes as values
        let userCountry: CountryISO;

        // Find the matching CountryISO enum value
        Object.keys(CountryISO).forEach((key) => {
          if (CountryISO[key].toLowerCase() === countryCode) {
            userCountry = CountryISO[key];
          }
        });

        if (userCountry) {
          // If the user's country is already in the array but not first, remove it
          this.selectedCountryISO = userCountry;
        }
      }
    } catch (error) {
      console.error('Error getting country from coordinates:', error);
    }
  }

  isBase64(dataUrl: string): boolean {
    if (!dataUrl) {
      return false;
    }
    const base64Regex = /^data:(.*?);base64,(.*)$/;
    const match = dataUrl.match(base64Regex);

    if (!match) {
      return false;
    }

    const base64String = match[2];
    const base64Pattern = /^[A-Za-z0-9+/]+={0,2}$/;

    return base64Pattern.test(base64String) && base64String.length % 4 === 0;
  }
}
